// SCR-STAFF-1000 — اتصال‌ها (US-INT-002، 050؛ AP-INT-02): جدول اتصال‌های پیکربندی‌شده مرکز (سامانه، سرویس، سطح شعبه/مرکز، وضعیت،
// آخرین آزمون)، فرم ایجاد/ویرایش با فیلدهای هدایت‌شده از Schema تنظیمات (AC-INT-002.1)، «آزمون اتصال» با نتیجه آنی، نشانی Webhook
// و تغییر آن، اعتبارنامه‌ها (فقط چهار نویسه آخر و تاریخ انقضا — C-AGENT-114) و نقشه سامانه‌های بیرونی با فاز (AC-INT-050.1).
// محتوای IsSample (درگاه نمونه) همیشه با نشان «نمونه» (CLAUDE.md §10).
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import NetworkCheckOutlinedIcon from '@mui/icons-material/NetworkCheckOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ConfigurableAdapterView,
  type ConnectionTestView,
  type IntegrationEndpointView,
  type ProviderCatalogItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  SampleBadge,
  SchemaFields,
  StatusChip,
  toJalali,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { WriteNotice } from '../settings/common';
import { AdapterName, intKeys } from './common';

export const SCR_STAFF_1000 = 'SCR-STAFF-1000';

/** نتیجه آخرین آزمون اتصال (برچسب کوتاه جدول). */
function LastTest({ endpoint }: { endpoint: IntegrationEndpointView }) {
  const { t } = useTranslation('int');
  if (endpoint.supportsConnectionTest === false) {
    return <span>{t('int:connections.test_not_supported')}</span>;
  }
  if (endpoint.lastTestOk === null || endpoint.lastTestOk === undefined) {
    return <span>{t('int:connections.not_tested')}</span>;
  }
  return (
    <Stack spacing={0.25}>
      <span data-testid={`endpoint-last-test-${endpoint.id}`} data-ok={String(endpoint.lastTestOk)}>
        {endpoint.lastTestOk
          ? t('int:connections.test_ok')
          : t('int:connections.test_failed', { code: endpoint.lastTestCode ?? '—' })}
      </span>
      {endpoint.lastTestAt ? (
        <Typography variant="caption" color="text.secondary">
          <JalaliDate value={endpoint.lastTestAt} withTime />
        </Typography>
      ) : null}
    </Stack>
  );
}

export function IntegrationsPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const write = useWriteAccess('int.endpoint.manage');
  const access = useAccess();
  const canManageSecrets = access.can('int.credential.manage') && write.canWrite;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<IntegrationEndpointView | null | undefined>(undefined);
  const [secretsFor, setSecretsFor] = useState<IntegrationEndpointView | null>(null);
  const [testResult, setTestResult] = useState<{ endpoint: IntegrationEndpointView; result: ConnectionTestView } | null>(
    null,
  );

  const endpoints = useQuery({
    queryKey: intKeys.endpoints(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/endpoints')),
  });
  const providers = useQuery({
    queryKey: intKeys.providers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/providers')),
  });

  const branchName = (id: string | null | undefined) =>
    id
      ? t('int:connections.scope_branch', {
          name: activeTenant?.branches.find((branch) => branch.branchId === id)?.name ?? '—',
        })
      : t('int:connections.scope_tenant');

  const test = useMutation({
    mutationFn: (endpoint: IntegrationEndpointView) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/endpoints/{id}:test', { params: { path: { id: endpoint.id } } }),
      ).then((result) => ({ endpoint, result })),
    onSuccess: (value) => {
      setTestResult(value);
      void queryClient.invalidateQueries({ queryKey: intKeys.endpoints(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<IntegrationEndpointView>[] = [
    {
      id: 'adapter',
      headerKey: 'int:connections.columns.adapter',
      cell: (row) => <AdapterName code={row.adapterCode} isSample={row.isSample} />,
    },
    { id: 'port', headerKey: 'int:connections.columns.port', cell: (row) => <bdi dir="ltr">{row.port}</bdi> },
    { id: 'scope', headerKey: 'int:connections.columns.scope', cell: (row) => branchName(row.branchId) },
    {
      id: 'priority',
      headerKey: 'int:connections.columns.priority',
      cell: (row) => <span data-testid={`endpoint-priority-${row.id}`}>{faDigits(Number(row.priority))}</span>,
    },
    {
      id: 'status',
      headerKey: 'int:connections.columns.status',
      cell: (row) => (
        <span data-testid={`endpoint-status-${row.id}`} data-active={String(row.isActive)}>
          <StatusChip tone={row.isActive ? 'active' : 'inactive'} />
        </span>
      ),
    },
    { id: 'last_test', headerKey: 'int:connections.columns.last_test', cell: (row) => <LastTest endpoint={row} /> },
    {
      id: 'actions',
      headerKey: 'int:connections.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.supportsConnectionTest !== false ? (
            <Tooltip title={t('int:connections.test')}>
              <span>
                <IconButton
                  aria-label={`${t('int:connections.test')} ${row.adapterCode}`}
                  onClick={() => test.mutate(row)}
                  disabled={!write.canWrite || test.isPending}
                  data-testid={`endpoint-test-${row.id}`}
                >
                  <NetworkCheckOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title={t('int:connections.edit')}>
            <span>
              <IconButton
                aria-label={`${t('int:connections.edit')} ${row.adapterCode}`}
                onClick={() => setEditing(row)}
                disabled={!write.canWrite}
                data-testid={`endpoint-edit-${row.id}`}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          {canManageSecrets ? (
            <Tooltip title={t('int:connections.secrets_title')}>
              <IconButton
                aria-label={`${t('int:connections.secrets_title')} ${row.adapterCode}`}
                onClick={() => setSecretsFor(row)}
                data-testid={`endpoint-secrets-${row.id}`}
              >
                <KeyOutlinedIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
  ];

  const adapterOf = (code: string) => providers.data?.configurable.find((adapter) => adapter.code === code);

  return (
    <ListPage
      screenId={SCR_STAFF_1000}
      titleKey="int:connections.title"
      subtitleKey="int:connections.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing(null)}
          disabled={!write.canWrite || !providers.data}
          data-testid="endpoint-create"
        >
          {t('int:connections.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {test.error ? <ErrorState error={test.error} compact /> : null}
          {testResult ? (
            <Alert
              severity={testResult.result.ok ? 'success' : 'error'}
              onClose={() => setTestResult(null)}
              data-testid="connection-test-result"
              data-ok={String(testResult.result.ok)}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <span>
                  {testResult.result.ok
                    ? t('int:connections.test_result_ok')
                    : t('int:connections.test_result_failed', {
                        code: testResult.result.error?.code ?? testResult.result.resultCode,
                        reason: testResult.result.error?.reason ?? testResult.result.resultCode,
                      })}
                </span>
                {testResult.result.isSample ? <SampleBadge /> : null}
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="int:connections.caption"
        columns={columns}
        rows={endpoints.data?.items}
        getRowId={(row) => row.id}
        isLoading={endpoints.isPending}
        error={endpoints.error}
        onRetry={() => void endpoints.refetch()}
        emptyTitleKey="int:connections.empty"
      />
      <ProviderCatalog items={providers.data?.catalog} isLoading={providers.isPending} error={providers.error} />
      {editing !== undefined && providers.data ? (
        <EndpointFormDialog
          endpoint={editing}
          adapters={providers.data.configurable}
          adapter={editing ? adapterOf(editing.adapterCode) : undefined}
          onClose={() => setEditing(undefined)}
        />
      ) : null}
      {secretsFor ? (
        <SecretsDialog
          endpoint={secretsFor}
          secretNames={adapterOf(secretsFor.adapterCode)?.secretNames ?? []}
          onClose={() => setSecretsFor(null)}
        />
      ) : null}
    </ListPage>
  );
}

/** نقشه سامانه‌های بیرونی با نوع، سطح اعتبارنامه و فاز (AC-INT-050.1). */
function ProviderCatalog({
  items,
  isLoading,
  error,
}: {
  items: readonly ProviderCatalogItem[] | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('int');
  const columns: DataTableColumn<ProviderCatalogItem>[] = [
    {
      id: 'system',
      headerKey: 'int:connections.catalog_columns.system',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`int:provider.${row.code}`, { defaultValue: row.code })}</span>
          {row.code === 'INT-PSP-SAMPLE' ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    {
      id: 'category',
      headerKey: 'int:connections.catalog_columns.category',
      cell: (row) => t(`int:category.${row.category}`, { defaultValue: row.category }),
    },
    {
      id: 'level',
      headerKey: 'int:connections.catalog_columns.level',
      cell: (row) => row.credentialLevels.map((level) => t(`int:level.${level}`, { defaultValue: level })).join(' · '),
    },
    {
      id: 'phase',
      headerKey: 'int:connections.catalog_columns.phase',
      cell: (row) =>
        row.phase === null ? t('int:connections.after_contract') : t('int:connections.coming', { n: faDigits(Number(row.phase)) }),
    },
    {
      id: 'status',
      headerKey: 'int:connections.catalog_columns.status',
      cell: (row) => (
        <Chip
          size="small"
          variant="outlined"
          data-testid={`provider-status-${row.code}`}
          color={row.available ? 'success' : 'default'}
          label={
            row.available
              ? t('int:connections.available')
              : row.credentialLevels.includes('Platform')
                ? t('int:connections.platform_managed')
                : row.phase === null
                  ? t('int:connections.after_contract')
                  : t('int:connections.coming', { n: faDigits(Number(row.phase)) })
          }
        />
      ),
    },
  ];
  return (
    <Stack spacing={1} sx={{ mt: 3 }}>
      <Typography variant="h6" component="h2">
        {t('int:connections.catalog_title')}
      </Typography>
      <DataTable
        captionKey="int:connections.catalog_caption"
        columns={columns}
        rows={items}
        getRowId={(row) => row.code}
        isLoading={isLoading}
        error={error}
      />
    </Stack>
  );
}

function EndpointFormDialog({
  endpoint,
  adapters,
  adapter: existingAdapter,
  onClose,
}: {
  /** null ← ایجاد. */
  endpoint: IntegrationEndpointView | null;
  adapters: readonly ConfigurableAdapterView[];
  adapter: ConfigurableAdapterView | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation('int');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const creating = endpoint === null;
  const [adapterCode, setAdapterCode] = useState(endpoint?.adapterCode ?? adapters[0]?.code ?? '');
  const [branchId, setBranchId] = useState(endpoint?.branchId ?? '');
  const [settings, setSettings] = useState<Record<string, unknown>>(
    (endpoint?.settings as Record<string, unknown> | undefined) ?? {},
  );
  const [isActive, setIsActive] = useState(endpoint?.isActive ?? false);
  // AP-INT-03 (US-INT-013، C-AGENT-1191): اولویت کمتر = مقدم؛ هنگام مدار باز، هاب به Endpoint فعال بعدی همان سطح سوئیچ می‌کند.
  const [priority, setPriority] = useState(String(endpoint?.priority ?? 100));
  const priorityValue = Number(priority);
  const priorityValid = /^[0-9]{1,4}$/.test(priority) && priorityValue >= 1 && priorityValue <= 1000;
  const [dirty, setDirty] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(endpoint?.webhookUrl ?? null);
  const adapter = creating ? adapters.find((item) => item.code === adapterCode) : existingAdapter;
  const requiresTest = adapter?.requiresConnectionTest ?? endpoint?.requiresConnectionTest ?? false;

  const save = useMutation({
    mutationFn: () =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/integration/endpoints', {
              body: { adapterCode, branchId: branchId || null, providerId: null, settings, isActive, priority: priorityValue },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/integration/endpoints/{id}', {
              params: { path: { id: endpoint.id } },
              body: { settings, isActive, priority: priorityValue, rowVersion: endpoint.rowVersion },
            }),
          ),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intKeys.endpoints(session?.tenantId) });
      onClose();
    },
  });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/endpoints/{id}:rotate-webhook-token', { params: { path: { id } } }),
      ),
    onSuccess: (view) => {
      setWebhookUrl(view.webhookUrl ?? null);
      void queryClient.invalidateQueries({ queryKey: intKeys.endpoints(session?.tenantId) });
    },
  });

  const change = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setDirty(true);
  };

  return (
    <FormDialog
      open
      titleKey={creating ? 'int:connections.create_title' : 'int:connections.edit_title'}
      screenId={SCR_STAFF_1000}
      onSubmit={() => {
        if (priorityValid) {
          save.mutate();
        }
      }}
      onClose={onClose}
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      {creating ? (
        <TextField
          select
          label={t('int:connections.choose_adapter')}
          value={adapterCode}
          onChange={(event) => {
            change(setAdapterCode)(event.target.value);
            setSettings({});
          }}
          slotProps={{ htmlInput: { 'data-testid': 'endpoint-adapter' } }}
        >
          {adapters.map((item) => (
            <MenuItem key={item.code} value={item.code} data-testid={`endpoint-adapter-option-${item.code}`}>
              {t(`int:adapter.${item.code}`, { defaultValue: item.code })}
              {item.isSample ? ` (${t('common:sample.badge')})` : ''}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <AdapterName code={endpoint.adapterCode} isSample={endpoint.isSample} />
      )}
      {adapter?.isSample ? <SampleBadge /> : null}
      <TextField
        select
        label={t('int:connections.branch')}
        value={branchId}
        onChange={(event) => change(setBranchId)(event.target.value)}
        disabled={!creating}
        slotProps={{ htmlInput: { 'data-testid': 'endpoint-branch' } }}
      >
        <MenuItem value="">{t('int:connections.branch_all')}</MenuItem>
        {(activeTenant?.branches ?? []).map((branch) => (
          <MenuItem key={branch.branchId} value={branch.branchId}>
            {branch.name}
          </MenuItem>
        ))}
      </TextField>
      <SchemaFields
        schema={adapter?.settingsSchema}
        value={settings}
        onChange={change(setSettings)}
        labelFor={(key) => t(`int:settings.${key}`, { defaultValue: key })}
        idPrefix="endpoint-setting"
      />
      <FormControlLabel
        control={
          <Switch
            checked={isActive}
            onChange={(event) => change(setIsActive)(event.target.checked)}
            slotProps={{ input: { 'data-testid': 'endpoint-active' } as object }}
          />
        }
        label={t('int:connections.activate')}
      />
      <TextField
        type="number"
        label={t('int:connections.priority')}
        value={priority}
        onChange={(event) => change(setPriority)(event.target.value)}
        error={!priorityValid}
        helperText={priorityValid ? t('int:connections.priority_hint') : t('int:connections.priority_invalid')}
        slotProps={{ htmlInput: { min: 1, max: 1000, dir: 'ltr', 'data-testid': 'endpoint-priority' } }}
      />
      {requiresTest ? (
        <Alert severity="info" data-testid="endpoint-requires-test">
          {t('int:connections.requires_test_hint')}
        </Alert>
      ) : null}
      {!creating && webhookUrl ? (
        <Stack spacing={1}>
          <TextField
            label={t('int:connections.webhook_url')}
            value={webhookUrl}
            helperText={t('int:connections.webhook_hint')}
            slotProps={{ htmlInput: { readOnly: true, dir: 'ltr', 'data-testid': 'endpoint-webhook-url' } }}
          />
          <div>
            <Button
              onClick={() => rotate.mutate(endpoint.id)}
              disabled={rotate.isPending}
              data-testid="endpoint-rotate-webhook"
            >
              {t('int:connections.rotate_token')}
            </Button>
          </div>
          {rotate.error ? <ErrorState error={rotate.error} compact /> : null}
        </Stack>
      ) : null}
    </FormDialog>
  );
}

interface SecretForm {
  value: string;
  expiresOn: string | null;
}

/** اعتبارنامه‌های یک اتصال: فقط چهار نویسه آخر و تاریخ انقضا نمایش داده می‌شود؛ مقدار تازه فقط نوشتنی. */
function SecretsDialog({
  endpoint,
  secretNames,
  onClose,
}: {
  endpoint: IntegrationEndpointView;
  secretNames: readonly string[];
  onClose: () => void;
}) {
  const { t } = useTranslation('int');
  const titleId = useId();
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>
        {t('int:connections.secrets_title')} — {t(`int:adapter.${endpoint.adapterCode}`, { defaultValue: endpoint.adapterCode })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('int:connections.secret_hint')}</Alert>
          {secretNames.length === 0 ? (
            <Typography>{t('int:connections.no_secrets')}</Typography>
          ) : (
            secretNames.map((name) => <SecretEditor key={name} endpoint={endpoint} name={name} />)
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="secrets-close">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SecretEditor({ endpoint, name }: { endpoint: IntegrationEndpointView; name: string }) {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const queryKey = intKeys.secrets(session?.tenantId, endpoint.id);
  const secrets = useQuery({
    queryKey,
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/integration/endpoints/{id}/secrets', { params: { path: { id: endpoint.id } } })),
  });
  const current = secrets.data?.items.find((item) => item.name === name);
  const { control, register, handleSubmit, reset, formState } = useForm<SecretForm>({
    defaultValues: { value: '', expiresOn: null },
  });
  const save = useMutation({
    mutationFn: (form: SecretForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/integration/endpoints/{id}/secrets/{name}', {
          params: { path: { id: endpoint.id, name } },
          body: { value: form.value, expiresAt: form.expiresOn ? `${form.expiresOn}T00:00:00Z` : null },
        }),
      ),
    onSuccess: () => {
      reset({ value: '', expiresOn: null });
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: intKeys.endpoints(session?.tenantId) });
    },
  });
  const label = t(`int:secret.${name}`, { defaultValue: name });
  return (
    <Stack
      component="form"
      spacing={1}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit((form) => save.mutate(form))();
      }}
      data-testid={`secret-${name}`}
    >
      <Typography variant="subtitle2" component="h3">
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`secret-state-${name}`}>
        {current
          ? current.last4
            ? t('int:connections.secret_saved', { last4: current.last4 })
            : t('int:connections.secret_saved_short')
          : t('int:connections.secret_missing')}
        {current?.expiresAt ? ` · ${t('int:connections.secret_expires_on', { date: toJalali(current.expiresAt) })}` : null}
      </Typography>
      <TextField
        type="password"
        autoComplete="new-password"
        label={`${label} — ${t('int:connections.secret_value')}`}
        {...register('value', { required: true })}
        error={Boolean(formState.errors.value)}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `secret-value-${name}` } }}
      />
      <JalaliDatePickerRHF name="expiresOn" control={control} labelKey="int:connections.secret_expires" />
      {save.error ? <ErrorState error={save.error} compact /> : null}
      <div>
        <Button type="submit" variant="outlined" disabled={save.isPending} data-testid={`secret-save-${name}`}>
          {t('int:connections.secret_save')}
        </Button>
      </div>
    </Stack>
  );
}
